import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type Dimensions = {
    width: number;
    height: number;
};

export const useDimensions = (): {
    ref: React.RefObject<HTMLDivElement>;
    dimensions: { width: number; height: number };
} => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState<Dimensions>({
        width: 0,
        height: 0,
    });
    const dimensionsRef = useRef<Dimensions>(dimensions);

    const getElementRefDimensions = (): { width: number; height: number } => {
        if (!elementRef.current) {
            return { width: 0, height: 0 };
        }
        const { width, height } = elementRef.current.getBoundingClientRect();
        return { width, height };
    };

    // Callback to update the dimensions with the current dimensions of the
    // elementRef.
    const updateDimensionsFromElementRef = useCallback(() => {
        const nextDimensions = getElementRefDimensions();
        if (
            nextDimensions.width !== dimensionsRef.current.width ||
            nextDimensions.height !== dimensionsRef.current.height
        ) {
            setDimensions(getElementRefDimensions);
        }
    }, [setDimensions]);

    // Attach handlers to window to detect resize events. This is useful for when
    // this component is not rerendered during resize events, but we still want
    // to adjust the static dimensions for the chart.
    useEffect(() => {
        window.addEventListener('resize', updateDimensionsFromElementRef);
        return (): void => {
            window.removeEventListener('resize', updateDimensionsFromElementRef);
        };
    }, [updateDimensionsFromElementRef]);

    // We call useLayoutEffect on every render to check for a dimension change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        dimensionsRef.current = dimensions;
        updateDimensionsFromElementRef();
    });

    return { ref: elementRef, dimensions };
};
